import {
    Camera,
    CheckCircle2,
    Eye,
    EyeOff,
    ImagePlus,
    Pencil,
    Save,
    Trash2,
    UserRound,
} from "lucide-react";
import {
    ChangeEvent,
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { AppDispatch, RootState } from "../../../../store";
import {
    updateUserProfile,
    addProfilePicture,
} from "../../../../Redux/custmoer_auth/slice";
import { uploadToCloudinary } from "../../../../utility/uploadToCloudinary";
import { UpdateProfileRequest } from "../../../../Redux/custmoer_auth/types";
import {
    fetchAllCountries,
    fetchStatesByCountryName,
    fetchDistrictsByStateName,
} from "../../../../Redux/location-mangment/slice";
import { Country, State, District } from "../../../../Redux/location-mangment/types";

import PersonalInfoDatePicker from "./PersonalInfoDatePicker";
import PersonalInfoSelect from "./PersonalInfoSelect";
import { genderOptions } from "./PersonalInformationData";
import "./PersonalInformationPage.css";

type PersonalInformationForm = {
    fullName: string;
    fatherName: string;
    invitationCode: string;
    invitorName: string;
    dateOfJoining: string;
    dateOfBirth: string;
    gender: string;
    mobileNo: string;
    email: string;
    country: string;
    state: string;
    district: string;
    city: string;
    pincode: string;
    transactionPassword: string;
};

type FormErrors = Partial<Record<keyof PersonalInformationForm, string>>;

const initialForm: PersonalInformationForm = {
    fullName: "",
    fatherName: "",
    invitationCode: "",
    invitorName: "",
    dateOfJoining: "",
    dateOfBirth: "",
    gender: "",
    mobileNo: "",
    email: "",
    country: "",
    state: "",
    district: "",
    city: "",
    pincode: "",
    transactionPassword: "",
};

const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) {
        return "";
    }

    const birthDate = new Date(`${dateOfBirth}T00:00:00`);
    if (Number.isNaN(birthDate.getTime())) {
        return "";
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (
        monthDifference < 0 ||
        (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
        age -= 1;
    }

    return age >= 0 ? String(age) : "";
};

const PersonalInformationPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        user,
        authloading,
    } = useSelector((state: RootState) => state.customerAuth);

    const {
        countries,
        states,
        districts,
        loading: locLoading,
    } = useSelector((state: RootState) => state.location);

    const [form, setForm] = useState<PersonalInformationForm>(initialForm);
    const [errors, setErrors] = useState<FormErrors>({});
    const [profileImage, setProfileImage] = useState<string>("");
    const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false);
    const [profileError, setProfileError] = useState<string>("");
    const [saved, setSaved] = useState<boolean>(false);
    const [showTransactionPassword, setShowTransactionPassword] = useState<boolean>(false);

    const eighteenYearsAgo = useMemo(() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 18);
        return d;
    }, []);

    const age = useMemo(() => calculateAge(form.dateOfBirth), [form.dateOfBirth]);

    // Derived location option lists from Redux store
    const countryOptions = useMemo(
        () =>
            countries
                .map((c: Country) => ({ value: c.countryName, label: c.countryName }))
                .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label)),
        [countries]
    );

    const stateOptions = useMemo(
        () =>
            states
                .map((s: State) => ({ value: s.stateName, label: s.stateName }))
                .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label)),
        [states]
    );

    const districtOptions = useMemo(
        () =>
            districts
                .map((d: District) => ({ value: d.districtName, label: d.districtName }))
                .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label)),
        [districts]
    );

    // Initial country fetch
    useEffect(() => {
        if (countries.length === 0 && !locLoading) {
            dispatch(fetchAllCountries());
        }
    }, [dispatch, countries.length, locLoading]);

    // Populate form data from Redux user state
    useEffect(() => {
        if (user) {
            setForm({
                fullName: user.fullName || "",
                fatherName: user.fatherName || "",
                invitationCode: user.inviterRegisterId || "",
                invitorName: user.inviterName || "",
                dateOfJoining: user.doj || "",
                dateOfBirth: user.dob || "",
                gender: user.gender || "",
                mobileNo: user.phone || "",
                email: user.email || user.emailId || "",
                country: user.country || "",
                state: user.state || "",
                district: user.district || "",
                city: user.city || "",
                pincode: user.zipCode || "",
                transactionPassword: "",
            });

            if (user.profilePicture) {
                setProfileImage(user.profilePicture);
            }

            if (user.country) {
                dispatch(fetchStatesByCountryName(user.country));
            }
            if (user.state) {
                dispatch(fetchDistrictsByStateName(user.state));
            }
        }
    }, [user, dispatch]);

    const updateField = <K extends keyof PersonalInformationForm>(
        key: K,
        value: PersonalInformationForm[K]
    ) => {
        setForm((previous) => ({
            ...previous,
            [key]: value,
        }));

        setErrors((previous) => {
            if (!previous[key]) {
                return previous;
            }
            const next = { ...previous };
            delete next[key];
            return next;
        });

        setSaved(false);
    };

    const handleCountryChange = (value: string) => {
        setForm((previous) => ({
            ...previous,
            country: value,
            state: "",
            district: "",
            city: "",
        }));

        setErrors((previous) => {
            const next = { ...previous };
            delete next.country;
            delete next.state;
            delete next.district;
            delete next.city;
            return next;
        });

        if (value) {
            dispatch(fetchStatesByCountryName(value));
        }

        setSaved(false);
    };

    const handleStateChange = (value: string) => {
        setForm((previous) => ({
            ...previous,
            state: value,
            district: "",
            city: "",
        }));

        setErrors((previous) => {
            const next = { ...previous };
            delete next.state;
            delete next.district;
            delete next.city;
            return next;
        });

        if (value) {
            dispatch(fetchDistrictsByStateName(value));
        }

        setSaved(false);
    };

    const handleDistrictChange = (value: string) => {
        setForm((previous) => ({
            ...previous,
            district: value,
            city: "",
        }));

        setErrors((previous) => {
            const next = { ...previous };
            delete next.district;
            delete next.city;
            return next;
        });

        setSaved(false);
    };

    const handleProfileFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
            setProfileError("Please choose a JPG, PNG or WEBP image.");
            event.target.value = "";
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setProfileError("Profile image must be 5 MB or smaller.");
            event.target.value = "";
            return;
        }

        setProfileError("");
        setIsUploadingPhoto(true);

        try {
            const imageUrl = await uploadToCloudinary(file);
            setProfileImage(imageUrl);
            await dispatch(addProfilePicture({ profilePicture: imageUrl })).unwrap();
            toast.success("Profile photo updated successfully!");
        } catch (err: any) {
            const msg = err.message || "Failed to upload image";
            setProfileError(msg);
            toast.error(msg);
        } finally {
            setIsUploadingPhoto(false);
            event.target.value = "";
        }
    };

    const removeProfileImage = async () => {
        try {
            setIsUploadingPhoto(true);
            await dispatch(addProfilePicture({ profilePicture: "" })).unwrap();
            setProfileImage("");
            setProfileError("");
            toast.success("Profile photo removed.");
        } catch (err: any) {
            toast.error(err.message || "Failed to remove profile photo");
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const validate = () => {
        const nextErrors: FormErrors = {};

        if (!form.fullName || form.fullName.trim().length < 2) {
            nextErrors.fullName = "Full name is required (at least 2 characters).";
        }

        if (!form.fatherName || form.fatherName.trim().length < 2) {
            nextErrors.fatherName = "Father's name is required (at least 2 characters).";
        }

        if (!form.dateOfBirth) {
            nextErrors.dateOfBirth = "Date of birth is required.";
        } else {
            const birthDate = new Date(`${form.dateOfBirth}T00:00:00`);
            const today = new Date();
            let ageNum = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                ageNum -= 1;
            }
            if (ageNum < 18) {
                nextErrors.dateOfBirth = "You must be at least 18 years old.";
            }
        }

        if (!form.gender) {
            nextErrors.gender = "Please select gender.";
        }

        if (!form.mobileNo || form.mobileNo.length !== 10) {
            nextErrors.mobileNo = "Enter a valid 10 digit mobile number.";
        }

        if (!form.country) {
            nextErrors.country = "Please select country.";
        }

        if (!form.state) {
            nextErrors.state = "Please select state.";
        }

        if (!form.district) {
            nextErrors.district = "Please select district.";
        }

        if (!form.city || form.city.trim().length < 2) {
            nextErrors.city = "City is required (at least 2 characters).";
        }

        if (!form.pincode || form.pincode.length !== 6 || form.pincode === "000000") {
            nextErrors.pincode = "Enter a valid 6 digit pincode.";
        }

        setErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            const firstErrorKey = Object.keys(nextErrors)[0] as keyof PersonalInformationForm;
            toast.error(nextErrors[firstErrorKey] || "Please fill all required fields correctly.");
            return false;
        }

        return true;
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!validate()) {
            return;
        }

        const requestData: UpdateProfileRequest = {
            data: {
                fullName: form.fullName.trim(),
                fatherName: form.fatherName.trim(),
                dob: form.dateOfBirth,
                gender: form.gender,
                country: form.country,
                state: form.state,
                district: form.district,
                city: form.city.trim(),
                zipCode: form.pincode,
                phone: form.mobileNo,
                ...(form.transactionPassword ? { transactionPassword: form.transactionPassword } : {}),
            },
        };

        try {
            await dispatch(updateUserProfile(requestData)).unwrap();
            setSaved(true);
            toast.success("Personal information updated successfully!", {
                autoClose: 3000,
            });
        } catch {
            // Error notification is dispatched by auth slice interceptor
        }
    };

    const handleReset = () => {
        if (user) {
            setForm({
                fullName: user.fullName || "",
                fatherName: user.fatherName || "",
                invitationCode: user.inviterRegisterId || "",
                invitorName: user.inviterName || "",
                dateOfJoining: user.doj || "",
                dateOfBirth: user.dob || "",
                gender: user.gender || "",
                mobileNo: user.phone || "",
                email: user.email || user.emailId || "",
                country: user.country || "",
                state: user.state || "",
                district: user.district || "",
                city: user.city || "",
                pincode: user.zipCode || "",
                transactionPassword: "",
            });

            if (user.country) {
                dispatch(fetchStatesByCountryName(user.country));
            }
            if (user.state) {
                dispatch(fetchDistrictsByStateName(user.state));
            }
        } else {
            setForm(initialForm);
        }

        setErrors({});
        setSaved(false);
    };

    return (
        <section className="personal-information-page">
            <div className="personal-information-shell">
                <aside className="personal-information-profile">
                    <div className="personal-profile-card">
                        <div className="personal-profile-avatar-wrap">
                            <div
                                className={`personal-profile-avatar ${profileImage ? "has-image" : ""}`}
                            >
                                {profileImage ? (
                                    <img
                                        src={profileImage}
                                        alt="Profile preview"
                                    />
                                ) : (
                                    <span>
                                        {user?.fullName?.charAt(0).toUpperCase() || form.fullName?.charAt(0).toUpperCase() || "S"}
                                    </span>
                                )}
                            </div>

                            <button
                                type="button"
                                className="personal-profile-primary-action"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploadingPhoto}
                            >
                                {profileImage ? (
                                    <>
                                        <Pencil size={15} strokeWidth={2} />
                                        Edit
                                    </>
                                ) : (
                                    <>
                                        <Camera size={15} strokeWidth={2} />
                                        Add
                                    </>
                                )}
                            </button>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="personal-profile-file-input"
                            onChange={handleProfileFile}
                        />

                        <div className="personal-profile-copy">
                            <strong>Profile photo</strong>
                            <span>
                                {isUploadingPhoto
                                    ? "Uploading..."
                                    : "JPG, PNG or WEBP • max 5 MB"}
                            </span>
                        </div>

                        {profileImage && (
                            <div className="personal-profile-image-actions">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploadingPhoto}
                                >
                                    <ImagePlus size={15} />
                                    Change photo
                                </button>

                                <button
                                    type="button"
                                    className="danger"
                                    onClick={removeProfileImage}
                                    disabled={isUploadingPhoto}
                                >
                                    <Trash2 size={15} />
                                    Delete
                                </button>
                            </div>
                        )}

                        {profileError && (
                            <p className="personal-profile-error">
                                {profileError}
                            </p>
                        )}
                    </div>
                </aside>

                <div className="personal-information-card">
                    <div className="personal-information-heading">
                        <div>
                            <span className="personal-information-kicker">
                                MY ACCOUNT
                            </span>
                            <h1>Personal Information</h1>
                            <p>
                                Keep your personal and contact details up to date.
                            </p>
                        </div>

                        <div className="personal-register-id">
                            <span>Register ID</span>
                            <strong>{user?.registerId || "N/A"}</strong>
                        </div>
                    </div>

                    {saved && (
                        <div className="personal-information-success">
                            <CheckCircle2 size={17} strokeWidth={2} />
                            <span>
                                Personal information saved successfully.
                            </span>
                        </div>
                    )}

                    <form
                        className="personal-information-form"
                        onSubmit={handleSubmit}
                        noValidate
                    >
                        <div className="personal-form-field personal-form-field-full">
                            <label htmlFor="personal-full-name">
                                Full Name
                                <span>(As per GOVT ID)</span>
                            </label>

                            <div className="personal-input-wrap">
                                <UserRound
                                    size={16}
                                    strokeWidth={1.8}
                                />
                                <input
                                    id="personal-full-name"
                                    type="text"
                                    value={form.fullName}
                                    readOnly
                                    className="read-only"
                                    placeholder="Enter Full Name (As per GOVT ID)"
                                />
                            </div>
                            {errors.fullName && (
                                <small className="personal-form-error">
                                    {errors.fullName}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-father-name">
                                Father's Name
                            </label>
                            <input
                                id="personal-father-name"
                                type="text"
                                value={form.fatherName}
                                onChange={(event) =>
                                    updateField(
                                        "fatherName",
                                        event.target.value
                                            .replace(/[^a-zA-Z\s]/g, "")
                                            .slice(0, 50)
                                    )
                                }
                                placeholder="Enter Father's Name"
                            />
                            {errors.fatherName && (
                                <small className="personal-form-error">
                                    {errors.fatherName}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-invitation-code">
                                Invitation Code
                            </label>
                            <input
                                id="personal-invitation-code"
                                type="text"
                                value={form.invitationCode}
                                readOnly
                                className="read-only"
                                placeholder="Enter Invitation Code"
                            />
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-invitor-name">
                                Invitor Name
                            </label>
                            <input
                                id="personal-invitor-name"
                                type="text"
                                value={form.invitorName}
                                readOnly
                                className="read-only"
                                placeholder="Enter Invitor Name"
                            />
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-date-joining">
                                Date of Joining
                            </label>
                            <input
                                id="personal-date-joining"
                                type="text"
                                value={
                                    form.dateOfJoining
                                        ? new Date(form.dateOfJoining).toLocaleDateString()
                                        : ""
                                }
                                readOnly
                                className="read-only"
                                placeholder="Date of Joining"
                            />
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-date-birth">
                                Date of Birth
                            </label>
                            <PersonalInfoDatePicker
                                id="personal-date-birth"
                                value={form.dateOfBirth}
                                placeholder="Select Date of Birth"
                                maxDate={eighteenYearsAgo}
                                minDate={new Date(1920, 0, 1)}
                                onChange={(value) =>
                                    updateField("dateOfBirth", value)
                                }
                            />
                            {errors.dateOfBirth && (
                                <small className="personal-form-error">
                                    {errors.dateOfBirth}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-gender">
                                Gender
                            </label>
                            <PersonalInfoSelect
                                id="personal-gender"
                                value={form.gender}
                                options={genderOptions}
                                placeholder="Select Gender"
                                onChange={(value) =>
                                    updateField("gender", value)
                                }
                            />
                            {errors.gender && (
                                <small className="personal-form-error">
                                    {errors.gender}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-age">
                                Age
                            </label>
                            <input
                                id="personal-age"
                                type="text"
                                value={age}
                                readOnly
                                placeholder="Calculated from Date of Birth"
                                className="read-only"
                            />
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-mobile">
                                Mobile No
                            </label>
                            <input
                                id="personal-mobile"
                                type="tel"
                                inputMode="numeric"
                                maxLength={10}
                                value={form.mobileNo}
                                onChange={(event) =>
                                    updateField(
                                        "mobileNo",
                                        event.target.value.replace(/\D/g, "")
                                    )
                                }
                                placeholder="Enter Mobile No"
                            />
                            {errors.mobileNo && (
                                <small className="personal-form-error">
                                    {errors.mobileNo}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-email">
                                E-mail ID
                            </label>
                            <input
                                id="personal-email"
                                type="email"
                                value={form.email}
                                readOnly
                                className="read-only"
                                placeholder="Enter E-mail ID"
                            />
                            {errors.email && (
                                <small className="personal-form-error">
                                    {errors.email}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-country">
                                Country
                            </label>
                            <PersonalInfoSelect
                                id="personal-country"
                                value={form.country}
                                options={countryOptions}
                                placeholder="Select Country"
                                loading={locLoading && countries.length === 0}
                                onChange={handleCountryChange}
                            />
                            {errors.country && (
                                <small className="personal-form-error">
                                    {errors.country}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-state">
                                State
                            </label>
                            <PersonalInfoSelect
                                id="personal-state"
                                value={form.state}
                                options={stateOptions}
                                placeholder={
                                    form.country
                                        ? "Select State"
                                        : "Select Country First"
                                }
                                disabled={!form.country}
                                loading={locLoading && states.length === 0}
                                onChange={handleStateChange}
                            />
                            {errors.state && (
                                <small className="personal-form-error">
                                    {errors.state}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-district">
                                District
                            </label>
                            <PersonalInfoSelect
                                id="personal-district"
                                value={form.district}
                                options={districtOptions}
                                placeholder={
                                    form.state
                                        ? "Select District"
                                        : "Select State First"
                                }
                                disabled={!form.state}
                                loading={locLoading && districts.length === 0}
                                onChange={handleDistrictChange}
                            />
                            {errors.district && (
                                <small className="personal-form-error">
                                    {errors.district}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-city">
                                City
                            </label>
                            <input
                                id="personal-city"
                                type="text"
                                value={form.city}
                                onChange={(event) =>
                                    updateField(
                                        "city",
                                        event.target.value
                                            .replace(/[^a-zA-Z\s]/g, "")
                                            .slice(0, 25)
                                    )
                                }
                                placeholder="Enter City"
                            />
                            {errors.city && (
                                <small className="personal-form-error">
                                    {errors.city}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field">
                            <label htmlFor="personal-pincode">
                                Pincode
                            </label>
                            <input
                                id="personal-pincode"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={form.pincode}
                                onChange={(event) => {
                                    const sanitized = event.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6);
                                    updateField("pincode", sanitized);
                                }}
                                placeholder="Enter Pincode"
                            />
                            {errors.pincode && (
                                <small className="personal-form-error">
                                    {errors.pincode}
                                </small>
                            )}
                        </div>

                        <div className="personal-form-field personal-form-field-full">
                            <label htmlFor="personal-transaction-password">
                                Transaction Password
                            </label>
                            <div className="personal-input-wrap">
                                <input
                                    id="personal-transaction-password"
                                    type={showTransactionPassword ? "text" : "password"}
                                    value={form.transactionPassword}
                                    onChange={(event) =>
                                        updateField(
                                            "transactionPassword",
                                            event.target.value.slice(0, 15)
                                        )
                                    }
                                    placeholder="Enter Transaction Password"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="personal-password-toggle"
                                    onClick={() =>
                                        setShowTransactionPassword(
                                            (current) => !current
                                        )
                                    }
                                    aria-label={
                                        showTransactionPassword
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                >
                                    {showTransactionPassword ? (
                                        <EyeOff size={16} />
                                    ) : (
                                        <Eye size={16} />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="personal-form-actions">
                            <button
                                type="button"
                                className="personal-reset-button"
                                onClick={handleReset}
                            >
                                Reset
                            </button>

                            <button
                                type="submit"
                                className="personal-save-button"
                                disabled={authloading}
                            >
                                {authloading ? (
                                    "Saving..."
                                ) : (
                                    <>
                                        <Save size={16} strokeWidth={2} />
                                        Submit Information
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default PersonalInformationPage;
